import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Room, RoomEvent } from 'livekit-client';
import { UserMenu } from '@/components/UserMenu';
import { InactivityTimeoutControl } from '@/components/InactivityTimeoutControl';
import {
  LiveKitRoom,
  useVoiceAssistant,
  useMultibandTrackVolume,
  RoomAudioRenderer,
  useRoomContext,
  useLocalParticipant,
  DisconnectButton,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';
import { Mic, MicOff, PhoneOff } from 'lucide-react';
import { useVoiceToken, useVoiceStatus, usePersonas } from '@/hooks/useVoiceApi';
import type { Persona, TokenResponse } from '@/types';

const LANG_FLAGS: Record<string, string> = {
  de: '🇩🇪', en: '🇬🇧', fr: '🇫🇷', es: '🇪🇸', it: '🇮🇹', pt: '🇵🇹',
  nl: '🇳🇱', pl: '🇵🇱', ru: '🇷🇺', zh: '🇨🇳', ja: '🇯🇵', ko: '🇰🇷',
};
function langFlag(code: string) {
  return LANG_FLAGS[code.toLowerCase()] ?? code.toUpperCase();
}

// ─── Custom audio waveform — equalizer bars driven by agent or user mic ──────

function Waveform({
  trackRef,
  micTrackRef,
  active,
  micActive,
}: {
  trackRef: ReturnType<typeof useVoiceAssistant>['audioTrack'];
  micTrackRef: Parameters<typeof useMultibandTrackVolume>[0];
  active: boolean;
  micActive: boolean;
}) {
  const BANDS = 9;
  const agentVolumes = useMultibandTrackVolume(trackRef, { bands: BANDS });
  const micVolumes = useMultibandTrackVolume(micTrackRef, { bands: BANDS });

  const volumes = micActive ? micVolumes : agentVolumes;
  const isActive = active || micActive;

  return (
    <div className="flex h-24 w-full max-w-sm items-center justify-center gap-1.5">
      {Array.from({ length: BANDS }).map((_, i) => {
        const raw = volumes[i] ?? 0;
        // Scale + clamp: idle baseline 8%, full-scale ~100%.
        const pct = Math.max(8, Math.min(100, raw * 140));
        return (
          <div
            key={i}
            className="w-1.5 rounded-full bg-white transition-[height,opacity] duration-100 ease-out"
            style={{
              height: `${pct}%`,
              opacity: isActive ? 0.9 : 0.35,
            }}
          />
        );
      })}
    </div>
  );
}

// ─── Inner component (has access to room context) ────────────────────────────

function CallUI({ persona, onDisconnect }: { persona: Persona | undefined; onDisconnect: () => void }) {
  const { state, audioTrack } = useVoiceAssistant();
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const [micEnabled, setMicEnabled] = useState(true);

  const isSpeaking = state === 'speaking';
  const isListening = state === 'listening';

  const micPublication = localParticipant.getTrackPublication(Track.Source.Microphone);
  const micTrackRef = micPublication
    ? { participant: localParticipant, publication: micPublication, source: Track.Source.Microphone as const }
    : undefined;

  async function toggleMic() {
    await room.localParticipant.setMicrophoneEnabled(!micEnabled);
    setMicEnabled((v) => !v);
  }

  const stateLabel: Record<string, string> = {
    connecting: 'Connecting…',
    initializing: 'Initializing…',
    listening: 'Listening',
    thinking: 'Thinking…',
    speaking: 'Speaking',
    disconnected: 'Disconnected',
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-between overflow-hidden bg-black px-6 py-12">
      {/* User menu — top right */}
      <div className="absolute top-3 right-4 flex items-center gap-3">
        <InactivityTimeoutControl compact />
        <UserMenu />
      </div>

      {/* Top: persona name + state */}
      <div className="text-center">
        <p className="text-xs uppercase tracking-widest text-white/30">Speaking with</p>
        <h1 className="mt-1 text-2xl font-semibold text-white">
          {persona?.name ?? 'Agent'}
          {persona?.language && (
            <span className="ml-2 text-xl align-middle">{langFlag(persona.language)}</span>
          )}
        </h1>
        <p className="mt-2 text-sm text-white/50">{stateLabel[state] ?? state}</p>
      </div>

      {/* Center: avatar with iPhone-style pulsing rings when speaking */}
      <div className="relative flex items-center justify-center" style={{ width: 240, height: 240 }}>
        {/* Pulse rings — only when agent is speaking */}
        {isSpeaking && (
          <>
            <div
              className="absolute rounded-full bg-white/5 animate-ping"
              style={{ width: 224, height: 224, animationDuration: '1.6s' }}
            />
            <div
              className="absolute rounded-full bg-white/[0.07] animate-ping"
              style={{ width: 188, height: 188, animationDuration: '1.6s', animationDelay: '0.35s' }}
            />
          </>
        )}

        {/* Avatar circle */}
        <div
          className={[
            'relative flex h-36 w-36 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 transition-all duration-500',
            isSpeaking
              ? 'border-white/60 shadow-[0_0_40px_rgba(255,255,255,0.12)]'
              : isListening
              ? 'border-white/30'
              : 'border-white/10',
          ].join(' ')}
        >
          {persona?.imageUrl ? (
            <img
              src={persona.imageUrl}
              alt={persona.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-4xl font-semibold text-white">
              {persona?.name?.[0]?.toUpperCase() ?? 'A'}
            </span>
          )}
        </div>
      </div>

      {/* Waveform visualizer */}
      <div className="flex w-full items-center justify-center">
        <Waveform trackRef={audioTrack} micTrackRef={micTrackRef} active={isSpeaking} micActive={isListening && micEnabled} />
      </div>

      {/* Bottom: mic toggle + hang-up */}
      <div className="flex items-center gap-6">
        <button
          onClick={toggleMic}
          className={[
            'flex h-14 w-14 items-center justify-center rounded-full border transition',
            micEnabled
              ? 'border-white/20 bg-white/10 text-white hover:bg-white/20'
              : 'border-red-500/50 bg-red-500/20 text-red-400',
          ].join(' ')}
          aria-label={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
        >
          {micEnabled ? <Mic className="h-6 w-6" /> : <MicOff className="h-6 w-6" />}
        </button>

        <DisconnectButton onClick={onDisconnect}>
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600 text-white transition hover:bg-red-700">
            <PhoneOff className="h-7 w-7" />
          </div>
        </DisconnectButton>
      </div>

      <RoomAudioRenderer />
    </div>
  );
}

// ─── Transcript overlay (separate from call UI to keep it clean) ─────────────

// ─── Page wrapper ─────────────────────────────────────────────────────────────

export function CallPage() {
  const { personaId } = useParams<{ personaId: string }>();
  const navigate = useNavigate();
  const { data: personas } = usePersonas();
  const { data: status } = useVoiceStatus();
  const tokenMutation = useVoiceToken();
  const { user } = useAuth0();

  const [connection, setConnection] = useState<TokenResponse | null>(null);
  const [room] = useState(() => new Room());

  const persona = personas?.find((p) => p.id === personaId);

  // Redirect back if services not ready
  useEffect(() => {
    if (status && status.asr !== 'InService') {
      navigate('/');
    }
  }, [status, navigate]);

  // Fetch token once on mount
  useEffect(() => {
    if (!personaId || connection) return;
    const callerName = user?.given_name ?? user?.name ?? user?.nickname;
    tokenMutation.mutate({ personaId, callerName }, {
      onSuccess: (data) => setConnection(data),
      onError: () => navigate('/personas'),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personaId]);

  // Navigate away on room disconnect
  useEffect(() => {
    const onDisconnected = () => navigate('/personas');
    room.on(RoomEvent.Disconnected, onDisconnected);
    return () => { room.off(RoomEvent.Disconnected, onDisconnected); };
  }, [room, navigate]);

  if (!connection || tokenMutation.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
          <p className="text-sm text-white/40">Connecting to {persona?.name ?? 'agent'}…</p>
        </div>
      </div>
    );
  }

  return (
    <LiveKitRoom
      room={room}
      serverUrl={connection.serverUrl}
      token={connection.token}
      audio={true}
      video={false}
      connect={true}
    >
      <CallUI
        persona={persona}
        onDisconnect={() => navigate('/personas')}
      />
    </LiveKitRoom>
  );
}
